import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";
import { Field, StatusPill } from "@ozpanel/kit";
import {
  Button,
  ConfirmDialog,
  Section,
  StateView,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useAuth,
  useToast,
} from "@ozpanel/ui";
import { TincHealthStrip, TincNetworkStatus } from "./TincNetworkStatus";
import { TincPeersPanel, type FloatingState } from "./TincPeers";

// The tinc mesh: the private network every other cluster service depends on.
//
// The module mounts 13 routes and v2 reached none. That matters more than the count suggests —
// Galera, GlusterFS, Redis replication and the peer channel all ride this mesh, so "is the mesh
// up, and does it know about every node" is the first question when anything cluster-wide breaks,
// and it could only be answered over SSH.
//
// The mesh itself is managed by the cluster module (join/evict maintain the host files), so this
// screen is deliberately mostly READ plus the two repairs that are safe: reload, and removing a
// host file for a node that is genuinely gone. Adding a node is a join, not a text box.

interface Network {
  name: string;
  unit?: string;
  running?: boolean;
  port?: number;
  mode?: string;
  interface?: string;
  iface_up?: boolean;
  address?: string;
  hosts?: string[] | null;
  is_mesh?: boolean;
  managed_by?: string;
  directives?: Record<string, string[]>;
  // mesh | floating | other — classified by the server from the network's own files (kinds.go).
  kind?: string;
  floating?: FloatingState;
}

// The two kinds of network a node runs are OPPOSITES (kinds.go), and fail differently: the cluster
// mesh is healthy when it reaches its peers; a floating-IP tunnel is healthy when this node's public
// address and default route still ride it. One list of both hid that — and hid the mesh's own
// dial-address collision of 2026-10-24 — so each kind is its own view, managed on its own.
type NetView = "mesh" | "floating" | "other";

function viewOf(n: Network): NetView {
  if (n.is_mesh || n.kind === "mesh") return "mesh";
  if (n.kind === "floating") return "floating";
  return "other";
}

type ScriptRow = {
  name: string;
  present: boolean;
  executable: boolean;
  mode?: string;
  bytes?: number;
  when?: string;
  managed_by?: string;
  managed_why?: string;
};

/** The scripts tincd runs, with the two facts that decide whether they do anything: is it THERE,
 *  and is it EXECUTABLE. A script saved 0644 is never run and tincd says nothing about it, so the
 *  mode is shown rather than presence alone — presence on its own reads as "configured". */
function ScriptList({ net, onEdit }: { net: string; onEdit: (v: { net: string; name: string; raw: string; managed?: string }) => void }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const q = useQuery({
    queryKey: ["tinc-scripts", net],
    queryFn: () => client.apiFetch<{ scripts: ScriptRow[] }>(`/tinc/networks/${encodeURIComponent(net)}/scripts`),
  });
  const open = async (name: string) => {
    try {
      const d = await client.apiFetch<{ raw?: string; managed_why?: string }>(
        `/tinc/networks/${encodeURIComponent(net)}/scripts/${encodeURIComponent(name)}`,
      );
      // Absent is a legitimate state — most of the six do not exist — so an empty read opens a
      // NEW script seeded with a shebang, rather than being refused like tinc.conf. The
      // difference is that here there is nothing to destroy.
      onEdit({ net, name, raw: d?.raw?.trim() ? d.raw : "#!/bin/sh\n", managed: d?.managed_why });
    } catch (e) {
      notify({ kind: "error", title: `Could not read ${name}`, body: e instanceof Error ? e.message : String(e) });
    }
  };
  return (
    <div className="mt-4">
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">
        Up/down scripts — tincd runs these as root
      </div>
      <StateView loading={q.isLoading} error={q.error} errorContext="the up/down scripts">
        <div className="divide-y divide-line rounded-lg border border-line">
          {(q.data?.scripts ?? []).map((sc: ScriptRow) => (
            <div key={sc.name} className="flex items-start justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[12px]">{sc.name}</span>
                  {!sc.present && <StatusPill tone="neutral">not present</StatusPill>}
                  {sc.present && sc.executable && <StatusPill tone="ok">{sc.mode}</StatusPill>}
                  {/* NOT executable is the silent one: the file is there, looks configured, and
                      tincd never runs it. It is the loudest thing on the row for that reason. */}
                  {sc.present && !sc.executable && (
                    <StatusPill tone="bad">{sc.mode} — not executable, tincd will not run it</StatusPill>
                  )}
                  {sc.managed_by && <StatusPill tone="accent">managed by {sc.managed_by}</StatusPill>}
                </div>
                <div className="mt-0.5 text-[11.5px] text-muted">{sc.when}</div>
                {sc.managed_why && <div className="mt-0.5 text-[11.5px] text-warn">{sc.managed_why}</div>}
              </div>
              <Button size="sm" variant="ghost" onClick={() => void open(sc.name)}>
                {sc.present ? "Edit" : "Create"}
              </Button>
            </div>
          ))}
        </div>
      </StateView>
    </div>
  );
}

export function TincOps() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [confirm, setConfirm] = React.useState<{ title: string; body: React.ReactNode; label: string; go: () => Promise<unknown> } | null>(null);
  const [monitorFor, setMonitorFor] = React.useState<string | null>(null);
  const [inviting, setInviting] = React.useState<string | null>(null);
  const [invite, setInvite] = React.useState<string | null>(null);
  const [addingHost, setAddingHost] = React.useState<{ net: string; name: string; body: string } | null>(null);
  // The name the invitation is FOR. tinc's `invite` takes the joining node's name and the server
  // requires it — this dialog sent no body at all and every invitation answered 400.
  const [inviteName, setInviteName] = React.useState("");
  // Creating and removing whole networks — present in v1, absent from v2 until now. A node can
  // run several: the cluster mesh, and others for their own purpose such as the floating-IP
  // network, each with its own tinc.conf.
  const [creatingNet, setCreatingNet] = React.useState<{ name: string; mode: string; port: string; address: string; vpn: string } | null>(null);
  const [deletingNet, setDeletingNet] = React.useState<string | null>(null);
  // The up/down scripts tincd runs as root. tinc-up is what gives the interface its address —
  // without it tincd runs, the unit is active, and the network routes nothing.
  const [scriptsFor, setScriptsFor] = React.useState<string | null>(null);
  const [editingScript, setEditingScript] = React.useState<{ net: string; name: string; raw: string; managed?: string } | null>(null);
  // Editing an existing host, and the whole tinc.conf — both present in v1, both absent from v2
  // until now, and both already served by the API.
  const [editingHost, setEditingHost] = React.useState<{ net: string; name: string; addresses: string; port: string; subnet: string } | null>(null);
  const [editingConf, setEditingConf] = React.useState<{ net: string; raw: string } | null>(null);

  const q = useQuery({
    queryKey: ["tinc-networks"],
    queryFn: () => client.apiFetch<{ networks: Network[] | null; version?: string }>("/tinc/networks"),
    retry: false,
  });

  const run = (label: string, p: Promise<unknown>) =>
    p
      .then(() => {
        notify({ kind: "success", title: label });
        void qc.invalidateQueries({ queryKey: ["tinc-networks"] });
        void qc.invalidateQueries({ queryKey: ["tinc-status"] });
        void qc.invalidateQueries({ queryKey: ["tinc-health"] });
        void qc.invalidateQueries({ queryKey: ["tinc-peers"] });
      })
      .catch((e: unknown) => notify({ kind: "error", title: `${label} failed`, body: e instanceof Error ? e.message : String(e) }));

  // EDIT a host, which v1 had and v2 lost. The server has always offered PUT .../hosts/{name} —
  // "edit a host's address/port/subnet (keeps its key)" — and nothing called it, so a peer that
  // changed address could only be forgotten and re-added, which throws away its key and needs the
  // peer's file again.
  const openEditHost = async (net: string, h: string) => {
    // PRELOADED from the host's current file. An editor that opens blank asks the operator to retype
    // what is already there and, worse, makes "leave it as it is" and "I did not look" the same
    // gesture — which on a subnet is the difference between routing to a peer and routing nowhere.
    try {
      const d = await client.apiFetch<{ address?: string; addresses?: string[] | null; port?: string; subnet?: string[] | null }>(
        `/tinc/networks/${encodeURIComponent(net)}/hosts/${encodeURIComponent(h)}`,
      );
      setEditingHost({
        net,
        name: h,
        // Every Address line, one per row: a peer reachable on several (a LAN address and a public
        // one) is dialled on each in turn.
        addresses: (d?.addresses?.length ? d.addresses : d?.address ? [d.address] : []).join("\n"),
        port: String(d?.port ?? ""),
        subnet: (d?.subnet ?? []).join(", "),
      });
    } catch (e) {
      notify({ kind: "error", title: `Could not read ${h}`, body: e instanceof Error ? e.message : String(e) });
    }
  };

  const forgetHost = (net: string, h: string, mesh: boolean) =>
    setConfirm({
      title: `Forget ${h} on ${net}?`,
      body: mesh ? (
        <p>
          Removes this node&apos;s host file for {h}, so the mesh stops trying to reach it. Do this only
          for a node that has genuinely been evicted — removing a live peer partitions it from this
          node, and Galera, the shared storage and Redis all ride this mesh.
        </p>
      ) : (
        <p>
          Removes this node&apos;s host file for {h} on {net}. On a floating-IP tunnel the hub is this
          node&apos;s default gateway: forgetting it takes the node&apos;s public address and default
          route down with the tunnel.
        </p>
      ),
      label: "Forget",
      go: () => client.apiFetch(`/tinc/networks/${encodeURIComponent(net)}/hosts/${encodeURIComponent(h)}`, { method: "DELETE" }),
    });

  const renderNetwork = (n: Network) => (
          <Section
            key={n.name}
            title={n.name}
            actions={
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="ghost" onClick={() => setMonitorFor(monitorFor === n.name ? null : n.name)}>
                  {monitorFor === n.name ? "Close status" : "Nodes, traffic & log"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setScriptsFor(scriptsFor === n.name ? null : n.name)}>
                  {scriptsFor === n.name ? "Close scripts" : "Up/down scripts"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setInviting(n.name)}>Invite a node</Button>
                {/* The MESH IS NOT DELETABLE and the button is not offered for it. The server
                    refuses it too — that refusal is the guard, this is only the courtesy of not
                    presenting an action that can only fail. Every other network is ordinary. */}
                {n.is_mesh ? (
                  <span className="text-[11.5px] text-muted">
                    the cluster mesh — cannot be removed here; remove a NODE with eviction
                  </span>
                ) : (
                  <Button size="sm" variant="danger" onClick={() => setDeletingNet(n.name)}>
                    Delete network
                  </Button>
                )}
                {/* Each network has its OWN tinc.conf — the cluster mesh and a floating-IP network
                    are configured differently, which is the whole reason networks are separate
                    things. The raw file is fetched when the editor opens so it is edited rather
                    than replaced from a blank box. */}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    let raw = "";
                    try {
                      // The file is at data.network.raw — NOT data.raw. Read from the live node
                      // rather than assumed: the first version of this guessed the top level,
                      // got undefined, and would have opened an EMPTY editor over a working mesh
                      // config. Writing that back blanks the file every cluster service rides.
                      const d = await client.apiFetch<{ network?: { raw?: string } }>(
                        `/tinc/networks/${encodeURIComponent(n.name)}`,
                      );
                      raw = String(d?.network?.raw ?? "");
                    } catch {
                      notify({ kind: "error", title: `Could not read ${n.name}'s tinc.conf` });
                      return;
                    }
                    if (!raw.trim()) {
                      // An EMPTY read is not an empty file — it is a shape this build did not
                      // understand, and the two are indistinguishable from here. Refusing to open
                      // is the only safe answer: the alternative offers a blank box whose Save
                      // destroys the mesh.
                      notify({
                        kind: "error",
                        title: `${n.name}'s tinc.conf came back empty`,
                        body: "Refusing to open an editor that would write a blank file over the mesh configuration.",
                      });
                      return;
                    }
                    setEditingConf({ net: n.name, raw });
                  }}
                >
                  Edit tinc.conf
                </Button>
                <Button size="sm" variant="ghost"
                  onClick={() => setConfirm({
                    title: `Reload ${n.name}?`,
                    body: (
                      <p>
                        Re-reads the host files without dropping the interface. Use it after a node
                        joined or was evicted and this node has not picked up the change — every
                        cluster service rides this mesh, so a reload is far less disruptive than a
                        restart.
                      </p>
                    ),
                    label: "Reload",
                    go: () => client.apiFetch(`/tinc/networks/${encodeURIComponent(n.name)}/reload`, { method: "POST" }),
                  })}>
                  Reload
                </Button>
              </div>
            }
          >
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill tone={n.running ? "ok" : "bad"}>{n.running ? "running" : "stopped"}</StatusPill>
              {/* The daemon running and the INTERFACE being up are different facts, and the second
                  is the one traffic depends on. A running tincd with a down interface routes
                  nothing while systemd reports the unit active. */}
              <StatusPill tone={n.iface_up ? "ok" : "bad"}>{n.iface_up ? `${n.interface} up` : `${n.interface} down`}</StatusPill>
              {viewOf(n) === "mesh" && <StatusPill tone="accent">cluster mesh</StatusPill>}
              {viewOf(n) === "floating" && <StatusPill tone="accent">floating-IP tunnel</StatusPill>}
            </div>

            <div className="mt-3 grid gap-x-8 gap-y-1 text-[12.5px] sm:grid-cols-2">
              <Row label="This node's address" value={n.address || "—"} />
              <Row label="Listen port" value={String(n.port ?? "—")} />
              <Row label="Mode" value={n.mode || "—"} />
              <Row label="Unit" value={n.unit || "—"} />
            </div>

            {viewOf(n) === "mesh" && (
              <p className="mt-2 max-w-3xl text-[11.5px] leading-relaxed text-muted">
                Managed by the cluster: nodes are added by joining, and every pass re-derives each
                peer&apos;s Address and Subnet lines here from the cluster&apos;s recorded addresses
                (a peer&apos;s own Port is kept) — an address or subnet edited below is put back on
                the next pass. To change where a node is dialled,
                change its addresses under{" "}
                <Link to="/modules/cluster" className="underline underline-offset-2">Cluster</Link>.
              </p>
            )}
            {viewOf(n) === "floating" && (
              <p className="mt-2 max-w-3xl text-[11.5px] leading-relaxed text-muted">
                {n.managed_by ? `Carries this node's ${n.managed_by}. ` : ""}This tunnel holds the
                node&apos;s public address and its default route: no tinc, no IP. It is set up by the
                floating-IP job under{" "}
                <Link to="/modules/cluster" className="underline underline-offset-2">Cluster</Link>; an
                edit here that breaks it strands the node.
              </p>
            )}
            {viewOf(n) === "other" && n.managed_by && (
              <p className="mt-2 max-w-3xl text-[11.5px] leading-relaxed text-muted">Managed by {n.managed_by}.</p>
            )}

            {/* Each host file's dial endpoints beside what the running tincd says of that peer, and
                the endpoints two peers share — GET /tinc/networks/{net}/peers. */}
            <TincPeersPanel
              net={n.name}
              onEdit={(h) => void openEditHost(n.name, h)}
              onForget={(h) => forgetHost(n.name, h, viewOf(n) === "mesh")}
              onAdd={() => setAddingHost({ net: n.name, name: "", body: "" })}
            />

            {scriptsFor === n.name && <ScriptList net={n.name} onEdit={setEditingScript} />}

            {monitorFor === n.name && <TincNetworkStatus net={n.name} />}
          </Section>
  );

  const nets = q.data?.networks ?? [];
  const byView: Record<NetView, Network[]> = {
    mesh: nets.filter((n) => viewOf(n) === "mesh"),
    floating: nets.filter((n) => viewOf(n) === "floating"),
    other: nets.filter((n) => viewOf(n) === "other"),
  };
  // The view is in the URL (?net=), beside the module page's own ?tab=, so a link can open either.
  const requested = searchParams.get("net");
  const view: NetView =
    requested === "floating" || (requested === "other" && byView.other.length > 0) ? requested : "mesh";
  const setView = (v: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("net", v);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      {/* Every network with its unit state measured from systemd — inactive and failed ones are
          listed, not dropped — and how many nodes each running daemon reaches. Opening one moves to
          its own view. */}
      <TincHealthStrip
        onOpen={(net) => {
          const n = nets.find((x) => x.name === net);
          if (n) setView(viewOf(n));
          setMonitorFor(net);
        }}
      />
      <StateView loading={q.isLoading} error={q.error} errorContext="the tinc networks">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[12.5px] text-muted">
            This node&apos;s tinc networks, by kind. The cluster mesh (
            <span className="font-mono">ozcluster</span>) carries Galera, the shared storage, Redis,
            the peer channel and the signed rollout; a floating-IP tunnel carries this node&apos;s
            public address. Each is measured and managed on its own.
          </p>
          <Button
            size="sm"
            onClick={() => setCreatingNet({ name: "", mode: "router", port: "", address: "", vpn: "" })}
          >
            New network
          </Button>
        </div>
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="mesh">Cluster mesh ({byView.mesh.length})</TabsTrigger>
            <TabsTrigger value="floating">Floating-IP tunnels ({byView.floating.length})</TabsTrigger>
            {byView.other.length > 0 && <TabsTrigger value="other">Other networks ({byView.other.length})</TabsTrigger>}
          </TabsList>
          <TabsContent value="mesh" className="space-y-6">
            {byView.mesh.length === 0 ? (
              <Section title="Cluster mesh">
                <div className="text-[13px] text-muted">
                  This node is not on the cluster mesh — no <span className="font-mono">ozcluster</span>{" "}
                  network is configured here. A node joins it by joining the cluster.
                </div>
              </Section>
            ) : (
              byView.mesh.map(renderNetwork)
            )}
          </TabsContent>
          <TabsContent value="floating" className="space-y-6">
            {byView.floating.length === 0 ? (
              <Section title="Floating-IP tunnels">
                <div className="text-[13px] text-muted">
                  No floating-IP tunnel on this node — it answers on its own provider address. A
                  floating address is set up under{" "}
                  <Link to="/modules/cluster" className="underline underline-offset-2">Cluster</Link>.
                </div>
              </Section>
            ) : (
              byView.floating.map(renderNetwork)
            )}
          </TabsContent>
          {byView.other.length > 0 && (
            <TabsContent value="other" className="space-y-6">
              {byView.other.map(renderNetwork)}
            </TabsContent>
          )}
        </Tabs>
      </StateView>

      <ConfirmDialog
        open={!!inviting}
        onOpenChange={(o) => !o && setInviting(null)}
        title="Create an invitation for a new node?"
        confirmLabel="Create it"
        body={
          <div className="space-y-2">
            <Field
              mono
              label="Name of the joining node"
              value={inviteName}
              placeholder="nnode12"
              onChange={setInviteName}
              help="tinc invites a NAMED node — the invitation it generates is that node's, and its host file will be created under this name."
            />
            <p className="text-[12.5px] leading-relaxed">
              Produces a one-time invitation the new machine uses to join this mesh. Treat it like
              a password: anyone holding it can put a machine inside the private network that
              Galera, GlusterFS and Redis all trust.
            </p>
          </div>
        }
        onConfirm={() => {
          const net = inviting;
          const who = inviteName.trim();
          setInviting(null);
          setInviteName("");
          if (!net) return;
          if (!who) {
            notify({ kind: "error", title: "An invitation needs the joining node's name" });
            return;
          }
          void client
            // The wire field is `invitation` — verified against node10, not guessed. Reading
            // `invite` fell through to "created", so the one-time string the whole feature exists
            // to produce was never shown and could not be recovered afterwards.
            .apiFetch<{ invitation?: string; node?: string; network?: string }>(`/tinc/networks/${encodeURIComponent(net)}/invite`, {
              method: "POST",
              // `name` is REQUIRED — tinc invites a named node, and the file it generates is
              // that node's. Sending nothing answered 400 "name is required" every time.
              body: JSON.stringify({ name: who }),
            })
            .then((r) => {
              // Shown until dismissed, never toasted: it is one-time and cannot be re-read.
              // An EMPTY string here means the response shape changed — say so rather than
              // present an empty box the operator would take for a failed invitation while the
              // server has already created a real one.
              const v = String(r?.invitation ?? "");
              if (!v) {
                notify({
                  kind: "error",
                  title: "The invitation was created but could not be read from the response",
                  body: "It exists on the node — check the tinc invitations there before creating another.",
                });
                return;
              }
              setInvite(v);
            })
            .catch((e: unknown) => notify({ kind: "error", title: "Could not create it", body: e instanceof Error ? e.message : String(e) }));
        }}
      />

      <ConfirmDialog
        open={!!invite}
        onOpenChange={(o) => !o && setInvite(null)}
        title="Invitation — shown only now"
        confirmLabel="I have copied it"
        body={
          <textarea readOnly value={invite ?? ""}
            className="h-28 w-full rounded border border-line bg-bg p-2 font-mono text-[11px]" />
        }
        onConfirm={() => setInvite(null)}
      />

      {/* EDIT AN EXISTING HOST — address, port, subnet, keeping its key.
          v1 had this (PUT .../hosts/{name} with {address, port, subnet}); v2 could only add and
          forget. Forgetting a live peer to change its address discards the key and needs the
          peer's file again, which on a mesh that Galera, GlusterFS and Redis all ride is a
          disproportionate way to correct a port. */}
      {/* CREATE A NETWORK. v1 had this; v2 listed networks and could not make one. */}
      {/* EDIT AN UP/DOWN SCRIPT. Saved 0755 and syntax-checked by the server before it lands —
          a script with a shell error makes the interface come up without an address while tincd
          keeps reporting the network as running. */}
      <ConfirmDialog
        open={!!editingScript}
        onOpenChange={(o) => !o && setEditingScript(null)}
        title={editingScript ? `${editingScript.name} — ${editingScript.net}` : ""}
        confirmLabel="Save it"
        destructive
        body={
          <div className="space-y-2">
            {editingScript?.managed && (
              <p className="rounded border border-warn/40 bg-warn/10 px-2 py-1.5 text-[12px] leading-relaxed">
                {editingScript.managed}
              </p>
            )}
            <textarea
              value={editingScript?.raw ?? ""}
              onChange={(e) => setEditingScript(editingScript ? { ...editingScript, raw: e.target.value } : null)}
              spellCheck={false}
              className="h-56 w-full rounded border border-line bg-bg p-2 font-mono text-[11.5px]"
            />
            <p className="text-[11.5px] leading-relaxed text-muted">
              Runs as <span className="font-mono">root</span> when tincd triggers it. It is saved{" "}
              <span className="font-mono">0755</span> — a script without the executable bit is
              silently never run. <span className="font-mono">$INTERFACE</span> is the tun/tap
              device; host and subnet scripts also get{" "}
              <span className="font-mono">$NODE</span>. Reload the network afterwards.
            </p>
          </div>
        }
        onConfirm={() => {
          const sc = editingScript;
          setEditingScript(null);
          if (!sc?.raw.trim()) return;
          void run(`${sc.name} saved`,
            client.apiFetch(`/tinc/networks/${encodeURIComponent(sc.net)}/scripts/${encodeURIComponent(sc.name)}`, {
              method: "PUT",
              body: JSON.stringify({ raw: sc.raw }),
            }));
        }}
      />

      <ConfirmDialog
        open={!!creatingNet}
        onOpenChange={(o) => !o && setCreatingNet(null)}
        title="Create a tinc network"
        confirmLabel="Create it"
        body={
          <div className="space-y-2">
            <Field mono label="Name" value={creatingNet?.name ?? ""} placeholder="floating"
              onChange={(v) => setCreatingNet(creatingNet ? { ...creatingNet, name: v } : null)}
              help="Letters, digits and underscores. This becomes /etc/tinc/<name> and the unit tinc@<name>." />
            <Field mono label="Port" value={creatingNet?.port ?? ""} placeholder="656"
              onChange={(v) => setCreatingNet(creatingNet ? { ...creatingNet, port: v } : null)}
              help="Must not collide with another network's port on this node — they all listen on the same interfaces." />
            <Field mono label="Address (optional)" value={creatingNet?.address ?? ""} placeholder="the address peers reach this node on"
              onChange={(v) => setCreatingNet(creatingNet ? { ...creatingNet, address: v } : null)}
              help="The PUBLIC address peers dial to reach this node. Written into its host file." />
            <Field mono label="This node's address ON the network" value={creatingNet?.vpn ?? ""} placeholder="10.9.0.1/24"
              onChange={(v) => setCreatingNet(creatingNet ? { ...creatingNet, vpn: v } : null)}
              help="Include the prefix length. This becomes the network's tinc-up — and tinc-up is the only thing that gives the interface an address. Leave it out and tincd will run, the unit will be active, and the network will route nothing." />
            <p className="text-[11.5px] leading-relaxed text-muted">
              A new network is empty: it has this node&apos;s key and no peers, so nothing is
              reachable over it until host files are exchanged or a node is invited.
            </p>
          </div>
        }
        onConfirm={() => {
          const c = creatingNet;
          setCreatingNet(null);
          if (!c?.name.trim()) return;
          void run(`${c.name} created`,
            client.apiFetch("/tinc/networks", {
              method: "POST",
              body: JSON.stringify({
                name: c.name.trim(),
                mode: "router",
                ...(c.port.trim() ? { port: Number(c.port) } : {}),
                ...(c.address.trim() ? { address: c.address.trim() } : {}),
                ...(c.vpn.trim() ? { vpn_address: c.vpn.trim() } : {}),
                start: true,
              }),
            }));
        }}
      />

      {/* DELETE A NETWORK — never the mesh; the server refuses that and the button is not shown
          for it. For any other network this is still destructive in a way that is easy to
          under-estimate, so the consequence is spelled out rather than summarised as "are you
          sure". */}
      <ConfirmDialog
        open={!!deletingNet}
        onOpenChange={(o) => !o && setDeletingNet(null)}
        title={deletingNet ? `Delete the ${deletingNet} network?` : ""}
        confirmLabel="Delete it"
        destructive
        body={
          <p>
            Stops and disables <span className="font-mono">tinc@{deletingNet}</span> and removes{" "}
            <span className="font-mono">/etc/tinc/{deletingNet}</span> entirely — its tinc.conf,
            every host file, and THIS NODE&apos;S PRIVATE KEY for that network. Peers keep their
            copy of the old key, so rejoining means exchanging host files again from scratch.
            Anything routed over this network stops immediately.
          </p>
        }
        onConfirm={() => {
          const n = deletingNet;
          setDeletingNet(null);
          if (!n) return;
          void run(`${n} deleted`,
            client.apiFetch(`/tinc/networks/${encodeURIComponent(n)}`, { method: "DELETE" }));
        }}
      />

      <ConfirmDialog
        open={!!editingHost}
        onOpenChange={(o) => !o && setEditingHost(null)}
        title={editingHost ? `Edit ${editingHost.name}` : ""}
        confirmLabel="Save"
        body={
          <div className="space-y-2">
            <label className="block text-[12px] text-muted">
              Addresses — one per line, tried in order
              <textarea value={editingHost?.addresses ?? ""} placeholder={"192.168.88.122\n116.203.11.62"}
                onChange={(e) => setEditingHost(editingHost ? { ...editingHost, addresses: e.target.value } : null)}
                className="mt-1 h-20 w-full rounded-lg border border-line2 bg-bg p-2 font-mono text-[11px] text-fg" />
            </label>
            <Field mono label="Port" value={editingHost?.port ?? ""} placeholder="655"
              onChange={(v) => setEditingHost(editingHost ? { ...editingHost, port: v } : null)} />
            <Field mono label="Subnets (comma separated)" value={editingHost?.subnet ?? ""} placeholder="100.64.0.2/32"
              onChange={(v) => setEditingHost(editingHost ? { ...editingHost, subnet: v } : null)} />
            <p className="text-[11.5px] leading-relaxed text-muted">
              Loaded from the host&apos;s current file, so what you see is what is in force. The
              key is kept. A subnet here is what this node will ROUTE to that peer — get it wrong
              and traffic for those addresses goes to the wrong node, or nowhere. Reload the
              network afterwards for tincd to re-read it.
            </p>
          </div>
        }
        onConfirm={() => {
          const e = editingHost;
          setEditingHost(null);
          if (!e) return;
          const body: Record<string, unknown> = {};
          // Empty keeps the file's addresses (the server does the same), never "no address at all".
          const addrs = e.addresses.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
          if (addrs.length > 0) body.addresses = addrs;
          if (e.port.trim()) body.port = e.port.trim();
          // An empty box means "leave it alone", so an empty subnet list is not sent — sending []
          // would clear the routes rather than keep them.
          const subs = e.subnet.split(",").map((x) => x.trim()).filter(Boolean);
          if (subs.length > 0) body.subnet = subs;
          void run(`${e.name} updated`,
            client.apiFetch(`/tinc/networks/${encodeURIComponent(e.net)}/hosts/${encodeURIComponent(e.name)}`,
              { method: "PUT", body: JSON.stringify(body) }));
        }}
      />

      {/* THE WHOLE tinc.conf, written verbatim — v1's words for it were "edited here it is written
          verbatim". Each network has its own, which is the point: the cluster mesh and a
          floating-IP network are configured differently. */}
      <ConfirmDialog
        open={!!editingConf}
        onOpenChange={(o) => !o && setEditingConf(null)}
        title={editingConf ? `tinc.conf for ${editingConf.net}` : ""}
        confirmLabel="Write it"
        destructive
        body={
          <div className="space-y-2">
            <textarea
              value={editingConf?.raw ?? ""}
              onChange={(e) => setEditingConf(editingConf ? { ...editingConf, raw: e.target.value } : null)}
              rows={12}
              className="w-full rounded-lg border border-line2 bg-bg px-3 py-2 font-mono text-[12px] text-fg"
              placeholder={"Name = nnode10\nAddressFamily = ipv4\nConnectTo = nnode11"}
            />
            <p className="text-[11.5px] leading-relaxed text-muted">
              Written verbatim — nothing here is validated or merged. This is the mesh every other
              service rides: an error takes the node off it, and the node cannot be reached to fix
              it afterwards. Reload the network only once you are sure.
            </p>
          </div>
        }
        onConfirm={() => {
          const e = editingConf;
          setEditingConf(null);
          if (!e || !e.raw.trim()) return;
          void run(`${e.net} tinc.conf written`,
            client.apiFetch(`/tinc/networks/${encodeURIComponent(e.net)}`,
              { method: "PUT", body: JSON.stringify({ raw: e.raw }) }));
        }}
      />

      <ConfirmDialog
        open={!!addingHost}
        onOpenChange={(o) => !o && setAddingHost(null)}
        title="Add a node's host file"
        confirmLabel="Add it"
        body={
          <div className="space-y-2">
            <label className="block text-[12px] text-muted">
              Node name
              <input value={addingHost?.name ?? ""} placeholder="nnode12"
                onChange={(e) => setAddingHost(addingHost ? { ...addingHost, name: e.target.value } : null)}
                className="mt-1 w-full rounded-lg border border-line2 bg-bg px-3 py-1.5 font-mono text-[12.5px] text-fg" />
            </label>
            <label className="block text-[12px] text-muted">
              Its host file
              <textarea value={addingHost?.body ?? ""} placeholder="Address = …&#10;Subnet = …&#10;-----BEGIN RSA PUBLIC KEY-----"
                onChange={(e) => setAddingHost(addingHost ? { ...addingHost, body: e.target.value } : null)}
                className="mt-1 h-28 w-full rounded-lg border border-line2 bg-bg p-2 font-mono text-[11px] text-fg" />
            </label>
            <p className="text-[11.5px] leading-relaxed text-muted">
              Normally the join does this for you. Adding one by hand puts a machine inside the
              private network every cluster service trusts, so only do it for a node you are
              deliberately admitting.
            </p>
          </div>
        }
        onConfirm={() => {
          const h = addingHost;
          setAddingHost(null);
          if (!h?.name.trim()) return;
          void run("Host file added", client.apiFetch(`/tinc/networks/${encodeURIComponent(h.net)}/hosts`, {
            method: "POST",
            // The wire field is `content`, which is what handleUpsertHost decodes and what the
            // route's own summary documents — `{name, content}`. This sent `body`, the local
            // state's name for the textarea, and the handler's write is guarded by
            // `if strings.TrimSpace(body.Content) != ""` — so an EMPTY host file was written,
            // the request returned 200, and the screen reported "Host file added".
            //
            // Proven both ways against node10 on 2026-08-31: posting {name, body} answers
            // {"ok":true,"saved":true} and leaves a zero-byte file; posting {name, content}
            // writes the Address and Subnet. An empty host file is worse than a missing one —
            // tinc reads it, finds no Address, and the peer is unreachable for a reason nothing
            // on the page can explain.
            body: JSON.stringify({ name: h.name.trim(), content: h.body }),
          }));
        }}
      />

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.title ?? ""}
        confirmLabel={confirm?.label ?? "Run"}
        destructive
        body={confirm?.body}
        onConfirm={() => {
          const c = confirm;
          setConfirm(null);
          if (c) void run(c.label, c.go());
        }}
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-line py-1">
      <span className="text-muted">{label}</span>
      <span className="text-right font-mono text-[12px]">{value}</span>
    </div>
  );
}
